import type { AxiosResponse } from 'axios'
import type { UnionToIntersection } from 'vue/types/common'
import type { ObjectEmitsOptions } from 'vue/types/v3-setup-context'

import { inject } from 'vue'

/**
 * Generic definition for a State that can
 * be declared on the Store state property
 */
export type StateTree = Record<string | number | symbol, any>

export type MutationMethod<S, V = any> = (state: S, value?: V) => void
export type MutationArgType<S, M> = M extends MutationMethod<S, infer T> ? T : never

export type ObjectCommitOptions = Record<string, ((...args: any[]) => any) | null>
export type CommitOptions = ObjectCommitOptions | string[]

export type CommitFn<Options = ObjectEmitsOptions, Mutation extends keyof Options = keyof Options> =
    Options extends Array<infer V>
        ? (event: V, ...args: any[]) => void
        : {} extends Options
          ? (event: string, ...args: any[]) => void
          : UnionToIntersection<
                {
                    [key in Mutation]: Options[key] extends (...args: infer Args) => any
                        ? (event: key, ...args: Args) => void
                        : (event: key, ...args: any[]) => void
                }[Mutation]
            >

export type Mutators<S> = {
    [key: string]: MutationMethod<S>
}
export type Getters<S> = {
    [key: string]: (state: S) => any
    // [key: string]: (state: S) => any
}

/**
 * Helper types to support intellisense on
 * Vuex for Vue 2
 */
export interface StoreContext<S, M extends Mutators<S>> /* extends _Commits<S, M> */ {
    mutators: M
    commit: (what: keyof M, value?: any) => void
    state: S
}

/**
 * Defines an action method that can be declared
 * as an action
 */
export type ActionMethod<S, M extends Mutators<S>, P = any, R = {}> = (
    contex: StoreContext<S, M>,
    payload?: P
) => Promise<R extends infer U ? U : never>

/**
 * The definition of a store.
 */
export interface StoreSettings<
    S extends StateTree = {},
    G extends Getters<S> = Getters<S>, // Record<string, (state: S) => any>,
    M extends Record<string, MutationMethod<S>> = Mutators<S>,
    A = Record<string, ActionMethod<S, M>>,
> {
    namespaced?: boolean
    getters?: G
    mutations?: M
    state: S
    actions: A
    modules?: Record<string, any>
}

export function defineStore<
    S extends StateTree = {},
    G extends Getters<S> = Getters<S>,
    M extends Mutators<S> = Mutators<S>,
>(config: StoreSettings<S, G, M>) {
    return config
}
// eslint-disable-next-line unused-imports/no-unused-vars
interface ActionAliasConstraint<T> {}
// eslint-disable-next-line unused-imports/no-unused-vars
export type VuexActionAlias<Result, Arguments = any> = string & ActionAliasConstraint<Result>

type VuexActionAliasMap = {
    [Alias: string]: string | VuexActionAlias<any>
}

type VuexActionMap<T extends VuexActionAliasMap> = {
    [Alias in keyof T]: T[Alias] extends VuexActionAlias<infer U, infer A>
        ? (args: A, option: 'explicit') => Promise<AxiosResponse<U>>
        : T[Alias] extends string
          ? (args: any, option: 'testing') => Promise<AxiosResponse>
          : never
}

/**
 * Opción de compatibilidad para vuex
 *
 * @param actions La lista de acciones a mapear
 * @returns El map que contiene las referencias de las funciones de vuex a ejecutar
 * @deprecated Considerar el uso de stores de Pinia como alternativa future-proof
 */
export function useMapActions(actions: VuexActionAliasMap): VuexActionMap<typeof actions> {
    const store = inject<{ dispatch: (action: string, argument?: any) => Promise<any> }>('store')
    if (store === undefined) {
        throw new Error('No Store was provided')
    }
    const output: Record<string, (value?: any) => Promise<any>> = {}

    Object.entries(actions).forEach(([mapped, target]) => {
        output[mapped] = (value?: any) => store.dispatch(target, value)
    })

    return output as any
}
